import { useState } from 'react';
import CustomerCombobox from '@/components/base/CustomerCombobox';
import { services as serviceNames, staffMembers, type Appointment, type SourceType } from '@/mocks/appointments';
import { services as serviceData } from '@/mocks/services';
import { customers as allCustomers } from '@/mocks/customers';

interface RowData {
  customerName: string;
  customerPhone: string;
  service: string;
  staff: string;
  duration: number;
  price: number;
}

interface Props {
  onClose: () => void;
  onSave: (appts: Appointment[]) => void;
  initialDate?: string;
  initialTime?: string;
}

const sources: SourceType[] = ['Google', 'Meta', 'WhatsApp', 'Tavsiye', 'Tekrar Müşteri'];

function getServiceInfo(name: string) {
  const s = serviceData.find(x => x.name === name);
  return { duration: s?.duration || 60, price: s?.price || 0 };
}

const emptyRow: RowData = {
  customerName: '',
  customerPhone: '',
  service: serviceNames[0],
  staff: staffMembers[0],
  duration: 60,
  price: 750,
};

export default function GroupAppointmentModal({ onClose, onSave, initialDate, initialTime }: Props) {
  const today = new Date().toISOString().split('T')[0];

  const [sharedDate, setSharedDate] = useState(initialDate || today);
  const [sharedTime, setSharedTime] = useState(initialTime || '10:00');
  const [sharedSource, setSharedSource] = useState<SourceType>('Google');
  const [sameService, setSameService] = useState(false);
  const [sharedService, setSharedService] = useState(serviceNames[0]);
  const [rows, setRows] = useState<RowData[]>([{ ...emptyRow }]);
  const [error, setError] = useState('');

  function updateRow(index: number, updates: Partial<RowData>) {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...updates } : r)));
    setError('');
  }

  function addRow() {
    const { duration, price } = sameService
      ? getServiceInfo(sharedService)
      : getServiceInfo(serviceNames[0]);
    setRows(prev => [
      ...prev,
      {
        customerName: '',
        customerPhone: '',
        service: sameService ? sharedService : serviceNames[0],
        staff: staffMembers[0],
        duration,
        price,
      },
    ]);
    setError('');
  }

  function removeRow(index: number) {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== index));
    setError('');
  }

  function handleToggleSameService(checked: boolean) {
    setSameService(checked);
    if (checked) {
      const { duration, price } = getServiceInfo(sharedService);
      setRows(prev => prev.map(r => ({ ...r, service: sharedService, duration, price })));
    }
  }

  function handleSharedServiceChange(svc: string) {
    setSharedService(svc);
    const { duration, price } = getServiceInfo(svc);
    setRows(prev => prev.map(r => ({ ...r, service: svc, duration, price })));
  }

  function handleSave() {
    const invalid = rows.find(r => !r.customerName.trim() || !r.staff);
    if (invalid) {
      setError('Lütfen tüm satırlarda müşteri ve personel seçin.');
      return;
    }
    const appts: Appointment[] = rows.map((row, idx) => ({
      id: `ap-${Date.now()}-${idx}`,
      customerName: row.customerName,
      customerPhone: row.customerPhone,
      service: row.service,
      staff: row.staff,
      date: sharedDate,
      time: sharedTime,
      duration: row.duration,
      source: sharedSource,
      notes: '',
      status: 'Bekliyor',
      paymentStatus: 'Açık Ödeme',
      price: row.price,
      whatsappConfirmed: false,
      createdAt: today,
      serviceItems: [{ serviceName: row.service, staff: row.staff, duration: row.duration, price: row.price }],
    }));
    onSave(appts);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={onClose} />
      <div
        className="relative rounded-spa w-full max-w-4xl max-h-[90vh] flex flex-col animate-scale-in border"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between p-4 border-b z-10 flex-shrink-0"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
        >
          <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
            Grup Randevusu
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Shared fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>
                Tarih
              </label>
              <input
                type="date"
                className="spa-input"
                value={sharedDate}
                onChange={e => setSharedDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>
                Saat
              </label>
              <input
                type="time"
                className="spa-input"
                value={sharedTime}
                onChange={e => setSharedTime(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>
                Kaynak
              </label>
              <select
                className="spa-input"
                value={sharedSource}
                onChange={e => setSharedSource(e.target.value as SourceType)}
              >
                {sources.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Same service toggle */}
          <div
            className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sameService}
                onChange={e => handleToggleSameService(e.target.checked)}
                className="w-4 h-4 cursor-pointer"
                style={{ accentColor: 'var(--gold)' }}
              />
              <span className="text-sm font-medium whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                Hepsi aynı hizmeti alsın
              </span>
            </label>
            {sameService && (
              <select
                className="spa-input text-sm flex-1 sm:max-w-xs"
                value={sharedService}
                onChange={e => handleSharedServiceChange(e.target.value)}
              >
                {serviceNames.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Rows */}
          <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1">
            {rows.map((row, index) => (
              <div
                key={index}
                className="p-3 rounded-lg border space-y-2"
                style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold" style={{ color: 'var(--gold)' }}>
                    Kişi {index + 1}
                  </span>
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(index)}
                      className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer transition-colors hover:bg-red-50"
                      title="Satırı sil"
                    >
                      <i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  {/* Müşteri */}
                  <div className="md:col-span-4">
                    <CustomerCombobox
                      customers={allCustomers.map(c => ({
                        id: c.id,
                        fullName: c.fullName,
                        phone: c.phone,
                        email: c.email,
                        status: c.status,
                      }))}
                      value={
                        row.customerName
                          ? { id: '', fullName: row.customerName, phone: row.customerPhone }
                          : null
                      }
                      onChange={customer => {
                        if (customer) {
                          updateRow(index, {
                            customerName: customer.fullName,
                            customerPhone: customer.phone,
                          });
                        }
                      }}
                      onCreateNew={(name, phone) =>
                        updateRow(index, { customerName: name, customerPhone: phone })
                      }
                      label="Müşteri"
                      required
                    />
                  </div>

                  {/* Hizmet */}
                  <div className="md:col-span-4">
                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                      Hizmet
                    </label>
                    <select
                      className="spa-input text-sm"
                      value={row.service}
                      disabled={sameService}
                      onChange={e => {
                        const svc = e.target.value;
                        const { duration, price } = getServiceInfo(svc);
                        updateRow(index, { service: svc, duration, price });
                      }}
                    >
                      {serviceNames.map(s => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Personel */}
                  <div className="md:col-span-4">
                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                      Personel
                    </label>
                    <select
                      className="spa-input text-sm"
                      value={row.staff}
                      onChange={e => updateRow(index, { staff: e.target.value })}
                    >
                      {staffMembers.map(s => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Süre & Fiyat */}
                  <div className="md:col-span-6">
                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                      Süre (dk)
                    </label>
                    <input
                      type="number"
                      className="spa-input text-sm"
                      value={row.duration}
                      onChange={e => updateRow(index, { duration: Number(e.target.value) })}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                      Tutar (₺)
                    </label>
                    <input
                      type="number"
                      className="spa-input text-sm"
                      value={row.price}
                      onChange={e => updateRow(index, { price: Number(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add row button */}
          <button
            onClick={addRow}
            className="w-full py-2.5 rounded-lg border border-dashed text-sm font-medium cursor-pointer transition-colors flex items-center justify-center gap-2"
            style={{ borderColor: 'var(--gold)', color: 'var(--gold)', background: 'rgba(201,169,98,0.06)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line" />
            </div>
            Terapist Ekle
          </button>

          {/* Error */}
          {error && (
            <div
              className="p-3 rounded-lg text-sm flex items-center gap-2"
              style={{ background: '#FEE2E2', color: '#DC2626' }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line" />
              </div>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t flex-shrink-0"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
        >
          <button onClick={onClose} className="spa-btn-secondary text-sm whitespace-nowrap">
            İptal
          </button>
          <button
            onClick={handleSave}
            className="spa-btn-primary text-sm flex items-center gap-2 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line text-sm" />
            </div>
            {rows.length} Randevuyu Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}